/**
 * VALIDADORES CENTRALIZADOS v2.3
 * Todas las validaciones de negocio en un solo lugar
 * ✅ v2.1: Conversión de Unidades Inteligente (kg <-> g, l <-> ml) + Fuzzy Match
 * ✅ v2.2: canCookMeal — fallback por nombre cuando no encuentra componente por ID.
 * ✅ v2.3: FIX MATEMÁTICO DE PORCIONES. Evita la multiplicación exponencial de stock.
 */
import { validators as baseValidators, MEAL_SOURCE_TYPE } from './types';

// ============================================
// HELPERS INTERNOS Y CONVERSORES
// ============================================
const isPositiveNumberSafe = (value, allowZero = false) => {
  if (value === null || value === undefined) return false;
  const num = Number(value);
  if (Number.isNaN(num)) return false;
  return allowZero ? num >= 0 : num > 0;
};

export const normalizeText = (text) => {
  if (!text) return '';
  return text
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .replace(/\s+/g, ' ');
};

const normalize = normalizeText;

export const normalizeToBase = (qty, un) => {
  const u = (un || 'unidad').toLowerCase().trim();
  const q = parseFloat(qty) || 0;
  if (u === 'kg') return { q: q * 1000, u: 'g' };
  if (u === 'l' || u === 'litro' || u === 'litros') return { q: q * 1000, u: 'ml' };
  if (u === 'gr') return { q, u: 'g' };
  if (u === 'cc') return { q, u: 'ml' };
  return { q, u };
};

export const isFuzzyMatch = (stockName, searchName) => {
  if (stockName === searchName) return true;
  if (stockName + 's' === searchName || stockName === searchName + 's') return true;
  if (stockName + 'es' === searchName || stockName === searchName + 'es') return true;
  try {
    const escapedSearch = searchName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`\\b${escapedSearch}\\b`, 'i');
    return regex.test(stockName);
  } catch (e) {
    if (stockName.length > 3 && searchName.length > 3) {
      return stockName.includes(searchName) || searchName.includes(stockName);
    }
    return false;
  }
};

export const findStockItem = (stock, ingredientName, unit) => {
  const searchName = normalizeText(ingredientName);
  const searchBase = normalizeToBase(1, unit);
  const candidates = stock.filter(item => {
    const itemBase = normalizeToBase(1, item.unit);
    return itemBase.u === searchBase.u;
  });
  return candidates.find(item => isFuzzyMatch(normalizeText(item.name), searchName));
};

// ============================================
// Buscar componente por ID con fallback por nombre
// ============================================
const findComponent = (components, compRef) => {
  // 1. Búsqueda exacta por ID (caso ideal)
  const byId = components.find(c => c.id === compRef.id);
  if (byId) return byId;

  // 2. Fallback: búsqueda por nombre normalizado
  if (!compRef.name) return null;
  const searchName = normalizeText(compRef.name);
  
  // 2a. Match exacto de nombre
  const exactName = components.find(c => normalizeText(c.name) === searchName);
  if (exactName) return exactName;

  // 2b. Match parcial (el nombre del compRef está contenido en el componente o viceversa)
  const partialName = components.find(c => {
    const compName = normalizeText(c.name);
    return compName.includes(searchName) || searchName.includes(compName);
  });
  return partialName || null;
};

// ============================================
// VALIDACIÓN DE STOCK
// ============================================
export const validateStockItem = (item) => {
  const errors = [];
  if (!item?.name || item.name.trim().length === 0) errors.push('El nombre es obligatorio');
  if (item?.name && item.name.trim().length > 100) errors.push('El nombre no puede superar 100 caracteres');
  if (!baseValidators.isValidCategory(item?.category)) errors.push('Categoría inválida');
  if (!isPositiveNumberSafe(item?.quantity, true)) errors.push('La cantidad debe ser un número mayor o igual a 0');
  if (!baseValidators.isValidUnit(item?.unit)) errors.push('Unidad inválida');
  return { isValid: errors.length === 0, errors };
};

export const canDeductFromStock = (stockItem, quantityToDeduct) => {
  if (!stockItem) return { canDeduct: false, reason: 'El ingrediente no existe en el stock' };
  if (!isPositiveNumberSafe(quantityToDeduct)) return { canDeduct: false, reason: 'Cantidad a descontar inválida' };
  const stockBase = normalizeToBase(stockItem.quantity, stockItem.unit);
  const deductBase = normalizeToBase(quantityToDeduct, stockItem.unit);
  if (stockBase.q < deductBase.q) {
    return {
      canDeduct: false,
      reason: `Stock insuficiente. Disponible: ${stockItem.quantity} ${stockItem.unit}, Necesario: ${quantityToDeduct} ${stockItem.unit}`,
      available: stockItem.quantity,
      needed: quantityToDeduct,
      missing: quantityToDeduct - stockItem.quantity
    };
  }
  return { canDeduct: true, available: stockItem.quantity, willRemain: stockItem.quantity - quantityToDeduct };
};

// ============================================
// VALIDACIÓN DE COMPONENTES
// ============================================
export const validateComponent = (component) => {
  const errors = [];
  if (!component?.name || component.name.trim().length === 0) errors.push('El nombre del componente es obligatorio');
  if (component?.name && component.name.trim().length > 50) errors.push('El nombre no puede superar 50 caracteres');
  if (!component?.type) errors.push('El tipo de componente es obligatorio');
  if (!component?.category) errors.push('La categoría es obligatoria');
  if (!Array.isArray(component?.ingredients)) errors.push('Los ingredientes deben ser un array');
  else if (component.ingredients.length === 0) errors.push('El componente debe tener al menos un ingrediente');
  if (Array.isArray(component?.ingredients)) {
    component.ingredients.forEach((ing, index) => {
      if (!ing?.name || ing.name.trim().length === 0) errors.push(`Ingrediente ${index + 1}: nombre obligatorio`);
      if (!isPositiveNumberSafe(ing?.quantity)) errors.push(`Ingrediente ${index + 1}: cantidad inválida`);
      if (!baseValidators.isValidUnit(ing?.unit)) errors.push(`Ingrediente ${index + 1}: unidad inválida`);
    });
  }
  return { isValid: errors.length === 0, errors };
};

// ============================================
// VALIDACIÓN DE RECETAS
// ============================================
export const validateRecipe = (recipe) => {
  const errors = [];
  if (!recipe?.name || recipe.name.trim().length === 0) errors.push('El nombre de la receta es obligatorio');
  if (recipe?.name && recipe.name.trim().length > 50) errors.push('El nombre no puede superar 50 caracteres');
  if (!recipe?.category) errors.push('La categoría es obligatoria');
  if (!isPositiveNumberSafe(recipe?.servings)) errors.push('Las porciones deben ser un número positivo');
  if (recipe?.servings && recipe.servings > 20) errors.push('Las porciones no pueden ser más de 20');
  if (!recipe?.main) errors.push('Debe tener un componente principal');
  if (recipe?.main && !recipe.main.componentId) errors.push('El componente principal debe tener un ID válido');
  if (recipe?.sides && Array.isArray(recipe.sides)) {
    recipe.sides.forEach((side, index) => {
      if (!side.componentId) errors.push(`Acompañamiento ${index + 1}: debe tener un ID válido`);
    });
  }
  return { isValid: errors.length === 0, errors };
};

export const validateIngredient = (ingredient) => {
  const errors = [];
  if (!ingredient?.name || ingredient.name.trim().length === 0) errors.push('El nombre del ingrediente es obligatorio');
  if (!isPositiveNumberSafe(ingredient?.quantity)) errors.push('La cantidad debe ser mayor a 0');
  if (!baseValidators.isValidUnit(ingredient?.unit)) errors.push('Unidad inválida');
  if (!baseValidators.isValidCategory(ingredient?.category)) errors.push('Categoría inválida');
  return { isValid: errors.length === 0, errors };
};

// ============================================
// VALIDACIÓN DE COMIDAS (MEALDATA)
// ============================================
export const validateMealData = (mealData) => {
  const errors = [];
  if (!mealData?.type) errors.push('El tipo de comida es obligatorio');
  if (mealData?.type && !baseValidators.isValidMealSourceType(mealData.type)) errors.push('Tipo de comida inválido');
  if (!mealData?.recipeName || mealData.recipeName.trim().length === 0) errors.push('El nombre de la comida es obligatorio');
  if (mealData?.type === MEAL_SOURCE_TYPE.RECIPE) {
    if (!mealData.recipeId) errors.push('Las recetas deben tener un recipeId');
    if (!mealData.components || mealData.components.length === 0) errors.push('Las recetas deben tener al menos un componente');
  }
  if (mealData?.type === MEAL_SOURCE_TYPE.COMBO) {
    if (!mealData.components || mealData.components.length === 0) errors.push('Los combos deben tener al menos un componente');
  }
  if (mealData?.components && Array.isArray(mealData.components)) {
    mealData.components.forEach((comp, index) => {
      if (!comp.id) errors.push(`Componente ${index + 1}: debe tener un ID`);
      if (!comp.name) errors.push(`Componente ${index + 1}: debe tener un nombre`);
      if (!isPositiveNumberSafe(comp.servings)) errors.push(`Componente ${index + 1}: porciones inválidas`);
    });
  }
  return { isValid: errors.length === 0, errors };
};

// ============================================
// PLANIFICACIÓN CON COMPONENTES (INTELIGENTE)
// ============================================
export const canCookMeal = (mealData, components, stock = []) => {
  if (!mealData) return { canCook: false, reason: 'Comida inválida' };

  if (mealData.type === MEAL_SOURCE_TYPE.MANUAL) {
    return { canCook: true, missingIngredients: [], warnings: [] };
  }

  if (!mealData.components || mealData.components.length === 0) {
    return { canCook: false, reason: 'La comida no tiene componentes definidos' };
  }

  const missingIngredients = [];
  const warnings = [];

  mealData.components.forEach(compRef => {
    const component = findComponent(components, compRef);

    if (!component) {
      console.warn('[canCookMeal] Componente no encontrado:', compRef.name, '(id:', compRef.id, ')');
      warnings.push({
        name: compRef.name,
        message: `Componente "${compRef.name}" no encontrado — actualizá los ejemplos`
      });
      return;
    }

    // ✅ FIX v2.3: Corrección matemática.
    // compRef.servings ya representa el total necesario para la comida planificada.
    // Solo aplicamos un ratio extra si el usuario está escalando la receta dinámicamente.
    const ratio = (mealData.baseServings && mealData.servings) ? (mealData.servings / mealData.baseServings) : 1;
    const multiplier = (compRef.servings / (component.servings || 1)) * ratio;

    (component.ingredients || component.items || []).forEach(ing => {
      const neededBase = normalizeToBase(ing.quantity * multiplier, ing.unit);
      const searchName = normalizeText(ing.name);

      const matchingStockItems = stock.filter(item => {
        const itemBase = normalizeToBase(1, item.unit);
        return itemBase.u === neededBase.u && isFuzzyMatch(normalizeText(item.name), searchName);
      });

      let availableBaseQty = 0;
      matchingStockItems.forEach(s => {
        const stockBase = normalizeToBase(s.quantity, s.unit);
        availableBaseQty += stockBase.q;
      });

      if (availableBaseQty < neededBase.q) {
        let missingVisualQty = neededBase.q - availableBaseQty;
        let visualUnit = neededBase.u;

        if (ing.unit === 'kg' && missingVisualQty >= 1000) { missingVisualQty = missingVisualQty / 1000; visualUnit = 'kg'; }
        else if ((ing.unit === 'l' || ing.unit === 'litro') && missingVisualQty >= 1000) { missingVisualQty = missingVisualQty / 1000; visualUnit = 'l'; }
        else if (ing.unit === 'kg' && missingVisualQty < 1000) { visualUnit = 'g'; }
        else if ((ing.unit === 'l' || ing.unit === 'litro') && missingVisualQty < 1000) { visualUnit = 'ml'; }
        else { visualUnit = ing.unit; }

        missingIngredients.push({
          name: ing.name,
          needed: neededBase.q,
          available: availableBaseQty,
          missing: missingVisualQty,
          unit: visualUnit
        });
      } else if ((availableBaseQty - neededBase.q) < 0.1) {
        warnings.push({ name: ing.name, message: 'Stock al límite para este ingrediente' });
      }
    });
  });

  return {
    canCook: missingIngredients.length === 0,
    missingIngredients,
    warnings,
    totalMissing: missingIngredients.length
  };
};

export const calculateMealIngredients = (mealData, components) => {
  if (!mealData || !mealData.components || mealData.components.length === 0) return [];

  const ingredientMap = {};

  mealData.components.forEach(compRef => {
    const component = findComponent(components, compRef);
    if (!component) return;

    // ✅ FIX v2.3: Misma corrección matemática que en canCookMeal
    const ratio = (mealData.baseServings && mealData.servings) ? (mealData.servings / mealData.baseServings) : 1;
    const multiplier = (compRef.servings / (component.servings || 1)) * ratio;

    (component.ingredients || component.items || []).forEach(ing => {
      const key = `${ing.name.toLowerCase()}_${ing.unit}`;
      if (!ingredientMap[key]) {
        ingredientMap[key] = { name: ing.name, quantity: 0, unit: ing.unit, category: ing.category || 'otros' };
      }
      ingredientMap[key].quantity += ing.quantity * multiplier;
    });
  });

  return Object.values(ingredientMap).map(ing => ({
    ...ing,
    quantity: Math.round(ing.quantity * 100) / 100
  }));
};

// ============================================
// VALIDACIÓN DE FECHA DE COMIDA
// ============================================
export const validateMealDate = (date) => {
  if (!baseValidators.isValidDate(date)) {
    return { isValid: false, error: 'Formato de fecha inválido (debe ser YYYY-MM-DD)' };
  }
  const [year, month, day] = date.split('-').map(Number);
  const mealDate = new Date(year, month - 1, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const maxDate = new Date(today);
  maxDate.setDate(maxDate.getDate() + 30);
  if (mealDate > maxDate) return { isValid: false, error: 'No se puede planificar más de 30 días en el futuro' };
  const minDate = new Date(today);
  minDate.setDate(minDate.getDate() - 7);
  if (mealDate < minDate) return { isValid: false, error: 'No se puede planificar comidas de hace más de 7 días' };
  return { isValid: true, date: mealDate };
};

// ============================================
// VALIDACIÓN DE LISTA DE COMPRAS
// ============================================
export const validateShoppingItem = (item) => {
  const errors = [];
  if (!item?.name || item.name.trim().length === 0) errors.push('El nombre es obligatorio');
  if (!isPositiveNumberSafe(item?.quantity)) errors.push('La cantidad debe ser mayor a 0');
  if (item?.adjustedQuantity !== undefined && !isPositiveNumberSafe(item.adjustedQuantity)) errors.push('La cantidad ajustada debe ser mayor a 0');
  if (!baseValidators.isValidUnit(item?.unit)) errors.push('Unidad inválida');
  if (!baseValidators.isValidCategory(item?.category)) errors.push('Categoría inválida');
  return { isValid: errors.length === 0, errors };
};

// ============================================
// VALIDACIONES DE NEGOCIO
// ============================================
export const businessValidations = {
  wouldLeaveNegativeStock: (currentQuantity, deductAmount) => {
    if (!isPositiveNumberSafe(currentQuantity, true)) return true;
    if (!isPositiveNumberSafe(deductAmount)) return true;
    return currentQuantity - deductAmount < 0;
  },
  isMealAlreadyCooked: (meal, mealType) => meal?.cooked?.[mealType] === true,
  isMealPlanned: (meal, mealType) => meal?.[mealType] !== null && meal?.[mealType] !== undefined,
  hasEnoughStockForMeal: (mealData, components, stock) => {
    const check = canCookMeal(mealData, components, stock);
    return check.canCook;
  }
};

// ============================================
// EXPORTS
// ============================================
export default {
  validateStockItem,
  canDeductFromStock,
  validateComponent,
  validateRecipe,
  validateIngredient,
  validateMealData,
  canCookMeal,
  calculateMealIngredients,
  validateMealDate,
  validateShoppingItem,
  findStockItem,
  normalizeText,
  businessValidations
};